import { Link } from '@tanstack/react-router';
import { m } from '../paraglide/messages.js';
import { isActive, useImports } from '../lib/imports.ts';
import { Badge } from '../ui/Badge.tsx';
import { Icon, type IconName } from '../ui/Icon.tsx';
import { useImportSheet } from './ImportSheetProvider.tsx';
import './TabBar.css';

type Tab = {
  to: '/recipes' | '/plan' | '/shopping' | '/settings';
  label: () => string;
  icon: IconName;
};

const left: Tab[] = [
  { to: '/recipes', label: m.nav_recipes, icon: 'book' },
  { to: '/plan', label: m.nav_plan, icon: 'calendar' },
];
const right: Tab[] = [
  { to: '/shopping', label: m.nav_shopping, icon: 'basket' },
  { to: '/settings', label: m.nav_settings, icon: 'sliders' },
];

function TabLink({ to, label, icon }: Tab) {
  return (
    <Link to={to} className="tab">
      <Icon name={icon} size="l" />
      <span className="tab__label">{label()}</span>
    </Link>
  );
}

/**
 * DESIGN.md §10 TabBar + ImportButton: five columns with the Import button in the centre
 * (never a FAB), which opens the Import sheet and counts imports in progress. From 1024 px
 * (iPad landscape, Mac) the same five destinations, in the same order, form a sidebar.
 */
export function TabBar() {
  const sheet = useImportSheet();
  const imports = useImports();
  const importCount = (imports.data ?? []).filter(isActive).length;
  return (
    <nav className="tab-bar" aria-label={m.nav_label()}>
      <span className="tab-bar__brand" aria-hidden="true">
        Dish<i>ly</i>
      </span>
      {left.map((tab) => (
        <TabLink key={tab.to} {...tab} />
      ))}
      <button
        type="button"
        className="import-button"
        aria-haspopup="dialog"
        onClick={() => sheet.open()}
      >
        <span className="import-button__pill">
          <Icon name="plus" />
        </span>
        <span className="tab__label">{m.nav_import()}</span>
        {importCount > 0 && (
          <Badge variant="count" className="import-button__count">
            {importCount}
          </Badge>
        )}
      </button>
      {right.map((tab) => (
        <TabLink key={tab.to} {...tab} />
      ))}
    </nav>
  );
}
